import React, { useState, useEffect, useContext, useMemo } from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import axios from "axios";
import moment from "moment";
import ReactHTMLTableToExcel from "react-html-table-to-excel";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Modal } from "react-bootstrap";

import { BASE_URL } from "../../../../BaseUrl";
import { _t, getCookie, getSystemSettings } from "../../../../functions/Functions";
import { ToastError } from "../../../../functions/ToastShow";
import { SettingsContext } from "../../../../contexts/Settings";
import AuditSidebar from "./AuditSidebar";
import RcrComplianceCharts from "./RcrComplianceCharts";
import RcrScoreTrendSection from "./RcrScoreTrendSection";
import "./RcrLeaderboardReport.css";

const CircularProgress = ({ value, color, size = 30, strokeWidth = 3.5 }) => {
  const radius = (size / 2) - strokeWidth;
  const circumference = 2 * Math.PI * radius;
  const validValue = Math.min(100, Math.max(0, parseFloat(value) || 0));
  const strokeDashoffset = circumference - (validValue / 100) * circumference;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="#f1f5f9"
        strokeWidth={strokeWidth}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={color || "#ef4444"}
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={strokeDashoffset}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </svg>
  );
};

const RcrLeaderboardReport = () => {
  const { t } = useTranslation();
  const { generalSettings } = useContext(SettingsContext);
  
  const [loading, setLoading] = useState(false);
  const [month, setMonth] = useState(moment().format("YYYY-MM"));
  const [data, setData] = useState(null);
  const [sortOrder, setSortOrder] = useState("critical");
  const [showObservationModal, setShowObservationModal] = useState(false);
  const [selectedBranchObservations, setSelectedBranchObservations] = useState(null);

  const handleOpenObservationModal = (branch) => {
    setSelectedBranchObservations(branch);
    setShowObservationModal(true);
  };

  useEffect(() => {
    fetchData(month);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async (selectedMonth) => {
    setLoading(true);
    try {
      const url = `${BASE_URL}/settings/consolidated-dashboard?month=${selectedMonth}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });
      // Assuming response.data contains the structure we need
      setData(response.data);
    } catch (error) {
      ToastError({ title: _t(t("Error fetching dashboard data")) });
    } finally {
      setLoading(false);
    }
  };
  const getResultStyle = (res) => {
    if (res === "FAIL") return { backgroundColor: "#fee2e2", color: "#b91c1c", fontWeight: "bold" };
    if (res === "PASS") return { backgroundColor: "#dcfce7", color: "#15803d", fontWeight: "bold" };
    return {};
  };

  const getResultBgColor = (res) => {
    if (res === "FAIL") return "#fee2e2";
    if (res === "PASS") return "#dcfce7";
    return "#ffffff";
  };

  const getScoreColor = (scoreStr) => {
    const score = parseFloat(scoreStr || 0);
    if (score < 50) return "#fee2e2"; // Reddish
    if (score < 60) return "#fef08a"; // Yellowish
    return "#dcfce7"; // Greenish
  };

  const branchesByRisk = data?.branches_ranked_by_risk;
  const sortedBranches = useMemo(() => {
    if (!branchesByRisk) return [];
    return [...branchesByRisk].sort((a, b) => {
      const scoreA = a.overall_score_raw ?? parseFloat(a.overall_score || 0);
      const scoreB = b.overall_score_raw ?? parseFloat(b.overall_score || 0);
      return sortOrder === "critical" ? scoreA - scoreB : scoreB - scoreA;
    });
  }, [branchesByRisk, sortOrder]);

  return (
    <>
      <Helmet>
        <title>{_t(t("Audit Leaderboard Report"))}</title>
      </Helmet>

      <main id="main">
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <AuditSidebar />
            </div>

            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              <div className="t-bg-white shadow-sm rounded">
                <div className="fk-scroll--pos-menu table-bottom-info-hide" data-simplebar>
                  <div className="p-4 rcr-dashboard-content">
                    <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom">
                      <div>
                      <h4 className="my-0 font-weight-bold rcr-dashboard-title">
                        {_t(t("Branch Compliance Dashboard"))}
                      </h4>
                      <p className="text-muted mb-0 sm-text d-flex align-items-center">
                        <i className="las la-chart-bar mr-1"></i> Auto-generated from branch audit data
                      </p>
                    </div>
                    <div className="d-flex align-items-center">
                      <div className="rcr-datepicker-pill">
                        <i className="las la-calendar text-primary mr-1 rcr-datepicker-icon"></i>
                        <label className="rcr-datepicker-label mt-1">Month</label>
                        <DatePicker
                          selected={month ? moment(month, "YYYY-MM").toDate() : new Date()}
                          onChange={(date) => setMonth(moment(date).format("YYYY-MM"))}
                          dateFormat="MMMM yyyy"
                          showMonthYearPicker
                          customInput={
                            <input 
                              className="border-0 bg-transparent p-0 m-0 shadow-none rcr-datepicker-input"
                            />
                          }
                        />
                      </div>

                      <button 
                        className="btn btn-sm btn-primary font-weight-bold shadow-sm d-flex justify-content-center align-items-center px-3 mr-2 rounded-pill rcr-action-btn"
                        onClick={() => fetchData(month)}
                        disabled={loading}
                      >
                        {loading ? (
                          <><i className="las la-spinner la-spin mr-1"></i> Fetching</>
                        ) : (
                          <><i className="las la-sync-alt mr-1"></i> Fetch</>
                        )}
                      </button>
                      
                      <ReactHTMLTableToExcel
                        id="excel_report"
                        className="btn btn-sm btn-success font-weight-bold shadow-sm d-flex justify-content-center align-items-center px-3 rounded-pill text-white rcr-action-btn"
                        table="rcr-leaderboard-table"
                        filename={`Branch_Compliance_Dashboard_${month}`}
                        sheet="Dashboard"
                        buttonText="Export"
                      />
                    </div>
                  </div>

                  {loading ? (
                    <div className="text-center py-5 my-5">
                      <i className="fa fa-spinner fa-spin fa-3x text-primary"></i>
                      <p className="mt-3 text-muted">Loading dashboard data...</p>
                    </div>
                  ) : (
                    <div className="dashboard-content-wrapper rcr-dashboard-content">
                      {/* UI Cards */}
                      <div className="row mb-2 mx-0">
                        {/* Branches Audited */}
                        <div className="col-md-4 mb-2">
                          <div className="card h-100 p-2 rcr-stat-card">
                            <div className="card-body p-2 d-flex flex-column justify-content-center">
                              <h4 className="font-weight-bold my-1 text-dark">
                                {data?.portfolio_snapshot?.branches_audited || 0}
                              </h4>
                              <p className="rcr-stat-label">Branches audited</p>
                            </div>
                          </div>
                        </div>

                        {/* Passed */}
                        <div className="col-md-4 mb-2">
                          <div className="card h-100 p-2 rcr-stat-card">
                            <div className="card-body p-2 d-flex">
                              <div className="d-flex align-items-center">
                                <div>
                                  <h4 className="font-weight-bold my-1 text-success">
                                    {data?.portfolio_snapshot?.branches_passed || 0}
                                  </h4>
                                  <p className="rcr-stat-label">Passed</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Failed */}
                        <div className="col-md-4 mb-2">
                          <div className="card h-100 p-2 rcr-stat-card">
                            <div className="card-body p-2 d-flex flex-column justify-content-center">
                              <h4 className="font-weight-bold my-1 text-danger">
                                {data?.portfolio_snapshot?.branches_failed || 0}
                              </h4>
                              <p className="rcr-stat-label">Failed</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="row mb-4 mx-0">
                        {/* Average overall score */}
                        <div className="col-md-4 mb-2">
                          <div className="card h-100 p-2 rcr-stat-card">
                            <div className="card-body p-2 d-flex flex-column justify-content-center">
                              <div className="d-flex align-items-center">
                                <h4 className="font-weight-bold my-1 text-dark mr-2 mb-0">
                                  {data?.portfolio_snapshot?.portfolio_avg_score || '0%'}
                                </h4>
                                <CircularProgress 
                                  value={parseFloat(data?.portfolio_snapshot?.portfolio_avg_score || 0)} 
                                  color={
                                    parseFloat(data?.portfolio_snapshot?.portfolio_avg_score || 0) >= 60
                                      ? "#16a34a"
                                      : parseFloat(data?.portfolio_snapshot?.portfolio_avg_score || 0) >= 50
                                      ? "#d97706"
                                      : "#ef4444"
                                  } 
                                  size={30}
                                  strokeWidth={3.5}
                                />
                              </div>
                              <p className="rcr-stat-label">Average overall score</p>
                            </div>
                          </div>
                        </div>

                        {/* Critical NCs */}
                        <div className="col-md-4 mb-2">
                          <div className="card h-100 p-2 rcr-stat-card">
                            <div className="card-body p-2 d-flex flex-column justify-content-center">
                              <h4 className="font-weight-bold my-1 text-danger">
                                {data?.portfolio_snapshot?.total_critical_ncs || 0}
                              </h4>
                              <p className="rcr-stat-label">Critical NCs</p>
                            </div>
                          </div>
                        </div>

                        {/* Gate failures */}
                        <div className="col-md-4 mb-2">
                          <div className="card h-100 p-2 rcr-stat-card">
                            <div className="card-body p-2 d-flex flex-column justify-content-center">
                              <h4 className="font-weight-bold my-1 text-danger">
                                {data?.portfolio_snapshot?.critical_gate_failures || 0}
                              </h4>
                              <p className="rcr-stat-label">Gate failures</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Graphical Representation (Charts) */}
                      <RcrComplianceCharts data={data} sortedBranches={sortedBranches} />

                      {/* Score Trend & Branch KPI Cards Section */}
                      <RcrScoreTrendSection 
                        scoreTrend={data?.score_trend}
                        branchCards={data?.branch_cards}
                      />

                      {/* Branch Highlights */}
                      <div className="mb-4">
                        <div className="d-flex flex-column">
                          {/* Most Critical Card */}
                          {data?.portfolio_snapshot?.most_critical_branch && (
                            <div className="card p-3 mb-3 rcr-highlight-card rcr-highlight-critical">
                              <div className="mb-1">
                                <span className="rcr-highlight-tag rcr-tag-critical">Most critical</span>
                              </div>
                              <h3 className="rcr-highlight-title">
                                {data.portfolio_snapshot.most_critical_branch.branch_name || data.portfolio_snapshot.most_critical_branch.name || '-'}
                              </h3>
                              <p className="rcr-highlight-desc">
                                {data.portfolio_snapshot.most_critical_branch.summary_text || '-'}
                              </p>
                            </div>
                          )}

                          {/* Best Performing Card */}
                          {data?.portfolio_snapshot?.best_performing_branch && (
                            <div className={`card p-3 ${data?.portfolio_snapshot?.weakest_category ? 'mb-3' : ''} rcr-highlight-card rcr-highlight-best`}>
                              <div className="mb-1">
                                <span className="rcr-highlight-tag rcr-tag-best">Best performing</span>
                              </div>
                              <h3 className="rcr-highlight-title">
                                {data.portfolio_snapshot.best_performing_branch.branch_name || data.portfolio_snapshot.best_performing_branch.name || '-'}
                              </h3>
                              <p className="rcr-highlight-desc">
                                {data.portfolio_snapshot.best_performing_branch.summary_text || '-'}
                              </p>
                            </div>
                          )}

                          {/* Weakest Category Card */}
                          {data?.portfolio_snapshot?.weakest_category && (
                            <div className="card p-3 rcr-highlight-card rcr-highlight-weakest">
                              <div className="mb-1">
                                <span className="rcr-highlight-tag rcr-tag-weakest">Weakest category</span>
                              </div>
                              <h3 className="rcr-highlight-title">
                                {data.portfolio_snapshot.weakest_category.category || data.portfolio_snapshot.weakest_category.name || '-'}
                              </h3>
                              <p className="rcr-highlight-desc">
                                {data.portfolio_snapshot.weakest_category.summary_text || (data.portfolio_snapshot.weakest_category.portfolio_avg_score ? `Portfolio average ${data.portfolio_snapshot.weakest_category.portfolio_avg_score}${data.portfolio_snapshot.weakest_category.weightage ? ` (weight ${data.portfolio_snapshot.weakest_category.weightage})` : ''}` : '-')}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Branches Ranked by Risk UI */}
                      <div className="rcr-section-box">
                        <h4 className="rcr-section-title">Branches ranked by risk</h4>
                        <p className="rcr-section-subtitle">Select a branch to see its category breakdown.</p>
                        
                        {/* Toggle Filter */}
                        <div className="rcr-filter-pill-group">
                          <button 
                            className={`rcr-filter-pill-btn ${sortOrder === 'critical' ? 'active shadow-sm' : ''}`}
                            onClick={() => setSortOrder('critical')}
                          >
                            Most critical first
                          </button>
                          <button 
                            className={`rcr-filter-pill-btn ${sortOrder === 'best' ? 'active shadow-sm' : ''}`}
                            onClick={() => setSortOrder('best')}
                          >
                            Best performing first
                          </button>
                        </div>

                        {/* List */}
                        <div className="rcr-branch-list">
                          {sortedBranches.map((branch, index) => (
                            <div 
                              key={branch.audit_id || index} 
                              className="rcr-branch-item"
                              onClick={() => handleOpenObservationModal(branch)}
                            >
                              <div className="rcr-branch-rank">
                                {index + 1}
                              </div>
                              <div className="rcr-branch-name-col">
                                <div className="rcr-branch-name">{branch.branch_name || branch.name}</div>
                              </div>
                              <div className="rcr-branch-btn-col">
                                <button
                                  type="button"
                                  className="rcr-obs-btn"
                                  title="View Observations"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenObservationModal(branch);
                                  }}
                                >
                                  <i className="fa fa-eye rcr-obs-btn-icon"></i>
                                </button>
                              </div>
                              <div className="rcr-branch-score-col">
                                <div className="rcr-branch-score">{branch.overall_score}</div>
                              </div>
                              <div className="rcr-branch-gates">
                                {branch.critical_gates?.map((gate, idx) => (
                                  <span 
                                    key={idx} 
                                    className={`rcr-gate-badge ${gate.gate_status === 'PASS' ? 'rcr-gate-pass' : (gate.gate_status === 'FAIL' ? 'rcr-gate-fail' : 'rcr-gate-unknown')}`} 
                                    title={gate.name || gate.code}
                                  >
                                    {gate.code ? gate.code.charAt(0).toUpperCase() : '?'}
                                  </span>
                                ))}
                              </div>
                              <div className="rcr-branch-ncs">
                                {branch.critical_ncs}
                              </div>
                            </div>
                          ))}
                          {sortedBranches.length === 0 && (
                            <div className="text-center text-muted py-4">No branches found.</div>
                          )}
                        </div>
                      </div>

                      {/* Category Heatmap UI */}
                      {data?.category_heatmap && data.category_heatmap.columns && data.category_heatmap.rows && (
                        <div className="rcr-section-box">
                          <h4 className="rcr-section-title rcr-section-title-lg">Category heatmap</h4>
                          <p className="rcr-section-subtitle">Branch scores by audit section, with section weight shown under each name. Red is low, green is high.</p>
                          
                          <div className="rcr-heatmap-container">
                            <table className="table table-borderless mb-0 rcr-heatmap-table">
                              <thead>
                                <tr>
                                  <th className="rcr-heatmap-col-branch">Branch Name</th>
                                  {data.category_heatmap.columns.map((col, idx) => (
                                    <th key={idx} className="rcr-heatmap-col-header">
                                      <div className="rcr-heatmap-category-title">
                                        {col.category}
                                      </div>
                                      <div className="rcr-heatmap-category-weight">
                                        {col.weightage}
                                      </div>
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {data.category_heatmap.rows.map((row, rowIdx) => (
                                  <tr key={rowIdx}>
                                    <td className="rcr-heatmap-row-branch">
                                      {row.branch_name}
                                    </td>
                                    {row.scores.map((score, colIdx) => {
                                      let ragClass = 'rcr-rag-grey';
                                      if (score.rag === 'red') ragClass = 'rcr-rag-red';
                                      else if (score.rag === 'green') ragClass = 'rcr-rag-green';
                                      else if (score.rag === 'amber' || score.rag === 'yellow') ragClass = 'rcr-rag-amber';
                                      
                                      return (
                                        <td key={colIdx} className={`rcr-heatmap-cell ${ragClass}`}>
                                          {score.score}
                                        </td>
                                      );
                                    })}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Hidden Table for Excel Export */}
                      <div className="d-none">
                        <div className="table-responsive">
                          <table id="rcr-leaderboard-table" className="table table-bordered" border="1" style={{ border: '1px solid #cbd5e1', borderCollapse: 'collapse' }}>
                            <tbody>
                              {/* Header */}
                              <tr>
                                <td colSpan={16} className="text-white text-uppercase font-weight-bold p-3" style={{ backgroundColor: '#0f172a', color: '#ffffff', fontSize: '16px', letterSpacing: '0.5px' }} bgcolor="#0f172a">
                                  <font color="#ffffff">BRANCH COMPLIANCE DASHBOARD</font>
                                </td>
                              </tr>
                              <tr>
                                <td colSpan={16} className="text-muted p-2 sm-text" style={{ fontStyle: 'italic', backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }} bgcolor="#f8fafc">
                                  {getSystemSettings(generalSettings, "siteName")} | Last refreshed: {moment().format('DD MMM YYYY')}
                                </td>
                              </tr>
                              <tr>
                                <td colSpan={16} style={{ height: '15px', border: 'none', backgroundColor: '#ffffff' }} bgcolor="#ffffff"></td>
                              </tr>

                              {/* Top Stats */}
                              <tr className="text-center font-weight-bold text-white sm-text">
                                <td align="center" colSpan={3} className="py-2 align-middle" style={{ backgroundColor: '#1e293b', color: '#ffffff', width: '21%' }} bgcolor="#1e293b"><font color="#ffffff">BRANCHES AUDITED</font></td>
                                <td align="center" colSpan={3} className="py-2 align-middle" style={{ backgroundColor: '#15803d', color: '#ffffff', width: '21%' }} bgcolor="#15803d"><font color="#ffffff">PASSED</font></td>
                                <td align="center" colSpan={3} className="py-2 align-middle" style={{ backgroundColor: '#b91c1c', color: '#ffffff', width: '21%' }} bgcolor="#b91c1c"><font color="#ffffff">FAILED</font></td>
                                <td align="center" colSpan={3} className="py-2 align-middle" style={{ backgroundColor: '#ca8a04', color: '#ffffff', width: '21%' }} bgcolor="#ca8a04"><font color="#ffffff">AVG OVERALL SCORE</font></td>
                                <td align="center" colSpan={2} className="py-2 align-middle" style={{ backgroundColor: '#b91c1c', color: '#ffffff', width: '16%' }} bgcolor="#b91c1c"><font color="#ffffff">TOTAL CRITICAL NCs</font></td>
                                <td align="center" colSpan={2} className="py-2 align-middle" style={{ backgroundColor: '#b91c1c', color: '#ffffff', width: '16%' }} bgcolor="#b91c1c"><font color="#ffffff">Gate Failures</font></td>
                              </tr>
                              <tr className="text-center font-weight-bold" style={{ fontSize: '26px', backgroundColor: '#f1f5f9' }} bgcolor="#f1f5f9">
                                <td align="center" colSpan={3} className="py-3" style={{ color: '#0f172a' }}>{data?.portfolio_snapshot?.branches_audited || 0}</td>
                                <td align="center" colSpan={3} className="py-3" style={{ color: '#15803d' }}>{data?.portfolio_snapshot?.branches_passed || 0}</td>
                                <td align="center" colSpan={3} className="py-3" style={{ color: '#b91c1c' }}>{data?.portfolio_snapshot?.branches_failed || 0}</td>
                                <td align="center" colSpan={3} className="py-3" style={{ color: '#ca8a04' }}>{data?.portfolio_snapshot?.portfolio_avg_score || '0%'}</td>
                                <td align="center" colSpan={2} className="py-3" style={{ color: '#b91c1c' }}>{data?.portfolio_snapshot?.total_critical_ncs || 0}</td>
                                <td align="center" colSpan={2} className="py-3" style={{ color: '#b91c1c' }}>{data?.portfolio_snapshot?.critical_gate_failures || 0}</td>
                              </tr>
                              <tr><td colSpan={16} style={{ height: '15px', border: 'none', backgroundColor: '#ffffff' }} bgcolor="#ffffff"></td></tr>

                              {/* Branch Highlights */}
                              <tr className="text-center font-weight-bold text-white sm-text">
                                <td align="center" colSpan={6} className="py-2" style={{ backgroundColor: '#dc2626', color: '#ffffff' }} bgcolor="#dc2626"><font color="#ffffff">⚠️ MOST CRITICAL BRANCH</font></td>
                                <td colSpan={2} style={{ border: 'none', backgroundColor: '#ffffff' }} bgcolor="#ffffff"></td>
                                <td align="center" colSpan={6} className="py-2" style={{ backgroundColor: '#16a34a', color: '#ffffff' }} bgcolor="#16a34a"><font color="#ffffff">✅ BEST PERFORMING BRANCH</font></td>
                              </tr>
                              <tr className="text-center font-weight-bold" style={{ fontSize: '18px' }}>
                                <td align="center" colSpan={6} className="py-2" style={{ backgroundColor: '#fee2e2', color: '#b91c1c' }} bgcolor="#fee2e2">
                                  {data?.portfolio_snapshot?.most_critical_branch?.branch_name || data?.portfolio_snapshot?.most_critical_branch?.name || '-'}
                                </td>
                                <td colSpan={2} style={{ border: 'none', backgroundColor: '#ffffff' }} bgcolor="#ffffff"></td>
                                <td align="center" colSpan={6} className="py-2" style={{ backgroundColor: '#dcfce7', color: '#15803d' }} bgcolor="#dcfce7">
                                  {data?.portfolio_snapshot?.best_performing_branch?.branch_name || data?.portfolio_snapshot?.best_performing_branch?.name || '-'}
                                </td>
                              </tr>
                              <tr className="text-center sm-text">
                                <td align="center" colSpan={6} className="py-2" style={{ backgroundColor: '#fee2e2', color: '#7f1d1d' }} bgcolor="#fee2e2">
                                  {data?.portfolio_snapshot?.most_critical_branch?.summary_text || '-'}
                                </td>
                                <td colSpan={2} style={{ border: 'none', backgroundColor: '#ffffff' }} bgcolor="#ffffff"></td>
                                <td align="center" colSpan={6} className="py-2" style={{ backgroundColor: '#dcfce7', color: '#14532d' }} bgcolor="#dcfce7">
                                  {data?.portfolio_snapshot?.best_performing_branch?.summary_text || '-'}
                                </td>
                              </tr>
                              <tr><td colSpan={16} style={{ height: '15px', border: 'none', backgroundColor: '#ffffff' }} bgcolor="#ffffff"></td></tr>

                              {/* Table 1: Branches Ranked by Risk */}
                              <tr>
                                <td colSpan={16} className="text-white font-weight-bold p-3" style={{ backgroundColor: '#1e293b', color: '#ffffff', fontSize: '14px', letterSpacing: '0.5px' }} bgcolor="#1e293b">
                                  <font color="#ffffff">BRANCHES RANKED BY RISK (most critical first)</font>
                                </td>
                              </tr>
                              <tr className="text-center text-white font-weight-bold sm-text" style={{ fontSize: '15px' }}>
                                <td align="center" className="py-2 align-middle" style={{ backgroundColor: '#334155', color: '#ffffff' }} bgcolor="#334155"><font color="#ffffff">Rank</font></td>
                                <td align="center" className="py-2 align-middle" style={{ backgroundColor: '#334155', color: '#ffffff' }} bgcolor="#334155"><font color="#ffffff">Audit Code</font></td>
                                <td align="center" className="py-2 align-middle" style={{ backgroundColor: '#334155', color: '#ffffff' }} bgcolor="#334155"><font color="#ffffff">Status</font></td>
                                <td align="center" className="py-2 align-middle" style={{ backgroundColor: '#334155', color: '#ffffff' }} bgcolor="#334155"><font color="#ffffff">Branch</font></td>
                                <td align="center" className="py-2 align-middle" style={{ backgroundColor: '#334155', color: '#ffffff' }} bgcolor="#334155"><font color="#ffffff">Overall Score</font></td>
                                <td align="center" className="py-2 align-middle" style={{ backgroundColor: '#334155', color: '#ffffff' }} bgcolor="#334155"><font color="#ffffff">Previous Audit Score</font></td>
                                <td align="center" className="py-2 align-middle" style={{ backgroundColor: '#334155', color: '#ffffff' }} bgcolor="#334155"><font color="#ffffff">Change (pp)</font></td>
                                <td align="center" className="py-2 align-middle" style={{ backgroundColor: '#334155', color: '#ffffff' }} bgcolor="#334155"><font color="#ffffff">Trend</font></td>
                                <td align="center" className="py-2 align-middle" style={{ backgroundColor: '#334155', color: '#ffffff' }} bgcolor="#334155"><font color="#ffffff">RAG</font></td>
                                <td align="center" className="py-2 align-middle" style={{ backgroundColor: '#334155', color: '#ffffff' }} bgcolor="#334155"><font color="#ffffff">Audit Result</font></td>
                                {data?.branches_ranked_by_risk?.[0]?.critical_gates?.map((gate, idx) => (
                                  <td key={idx} align="center" className="py-2 align-middle" style={{ backgroundColor: '#334155', color: '#ffffff' }} bgcolor="#334155">
                                    <font color="#ffffff">{gate.name}</font>
                                  </td>
                                ))}
                                <td align="center" className="py-2 align-middle" style={{ backgroundColor: '#334155', color: '#ffffff' }} bgcolor="#334155"><font color="#ffffff">Critical NCs</font></td>
                              </tr>
                              {data?.branches_ranked_by_risk?.map((branch, index) => (
                                <tr key={index} className="text-center align-middle" style={{ backgroundColor: '#ffffff', fontSize: '15px' }} bgcolor="#ffffff">
                                  <td align="center" className="py-2 text-muted font-weight-bold">{index + 1}</td>
                                  <td align="center" className="py-2 font-weight-bold">{branch.audit_code || '-'}</td>
                                  <td align="center" className="py-2 font-weight-bold">{branch.status || '-'}</td>
                                  <td align="center" className="py-2 font-weight-bold" style={{ color: '#0f172a' }}>{branch.name || branch.branch_name || '-'}</td>
                                  <td align="center" className="py-2 font-weight-bold" style={{ backgroundColor: getScoreColor(branch.overall_score), color: '#0f172a' }} bgcolor={getScoreColor(branch.overall_score)}>{branch.overall_score}</td>
                                  <td align="center" className="py-2 font-weight-bold" style={{ color: '#0f172a' }}>{branch.previous_audit_score || '-'}</td>
                                  <td align="center" className="py-2 font-weight-bold" style={{ color: '#0f172a' }}>{branch.change_pp ?? branch.change ?? '-'}</td>
                                  <td align="center" className="py-2 font-weight-bold" style={{ color: '#0f172a' }}>{branch.trend || '-'}</td>
                                  <td align="center" className="py-2 font-weight-bold" style={{ color: branch.rag_code === 'red' ? '#dc2626' : (branch.rag_code === 'amber' ? '#ca8a04' : '#16a34a') }}>
                                    {branch.rag || 'Red'}
                                  </td>
                                  <td align="center" className="py-2" style={{ ...getResultStyle(branch.audit_result), border: branch.audit_result === 'FAIL' ? '2px solid #ef4444' : '' }} bgcolor={getResultBgColor(branch.audit_result)}>
                                    {branch.audit_result}
                                  </td>
                                  {branch.critical_gates?.map((gate, idx) => (
                                    <td key={idx} align="center" className="py-2" style={getResultStyle(gate.gate_status)} bgcolor={getResultBgColor(gate.gate_status)}>
                                      {gate.gate_status}
                                    </td>
                                  ))}
                                  <td align="center" className="py-2 font-weight-bold" style={{ color: branch.critical_ncs > 0 ? '#b91c1c' : '#15803d' }}>{branch.critical_ncs}</td>
                                </tr>
                              ))}
                              <tr><td colSpan={16} style={{ height: '15px', border: 'none', backgroundColor: '#ffffff' }} bgcolor="#ffffff"></td></tr>

                              {/* Priority Action */}
                              <tr>
                                <td colSpan={16} className="text-white font-weight-bold p-3" style={{ backgroundColor: '#b91c1c', color: '#ffffff', fontSize: '14px', letterSpacing: '0.5px' }} bgcolor="#b91c1c">
                                  <font color="#ffffff">PRIORITY ACTION</font>
                                </td>
                              </tr>
                              <tr>
                                <td colSpan={16} className="p-3" style={{ backgroundColor: '#fef2f2', color: '#7f1d1d', borderLeft: '4px solid #b91c1c' }} bgcolor="#fef2f2">
                                  {data?.portfolio_snapshot?.priority_action || '-'}
                                </td>
                              </tr>
                              <tr><td colSpan={16} style={{ height: '15px', border: 'none', backgroundColor: '#ffffff' }} bgcolor="#ffffff"></td></tr>

                              {/* Category Risk */}
                              <tr>
                                <td colSpan={16} className="text-white font-weight-bold p-3" style={{ backgroundColor: '#1e293b', color: '#ffffff', fontSize: '14px', letterSpacing: '0.5px' }} bgcolor="#1e293b">
                                  <font color="#ffffff">CATEGORY-LEVEL RISK ACROSS ALL BRANCHES</font>
                                </td>
                              </tr>
                              <tr className="text-center text-white font-weight-bold sm-text" style={{ fontSize: '15px' }}>
                                <td align="center" colSpan={4} className="py-2" style={{ backgroundColor: '#334155', color: '#ffffff' }} bgcolor="#334155"><font color="#ffffff">Category</font></td>
                                <td align="center" colSpan={3} className="py-2" style={{ backgroundColor: '#334155', color: '#ffffff' }} bgcolor="#334155"><font color="#ffffff">Weight</font></td>
                                <td align="center" colSpan={4} className="py-2" style={{ backgroundColor: '#334155', color: '#ffffff' }} bgcolor="#334155"><font color="#ffffff">Portfolio Avg Score</font></td>
                                <td align="center" colSpan={3} className="py-2" style={{ backgroundColor: '#334155', color: '#ffffff' }} bgcolor="#334155"><font color="#ffffff">RAG</font></td>
                              </tr>
                              {(data?.category_level_risk?.vcategory || data?.category_level_risk?.categories)?.map((cat, idx) => (
                                <tr key={idx} className="text-center align-middle" style={{ backgroundColor: '#ffffff', fontSize: '15px' }} bgcolor="#ffffff">
                                  <td colSpan={4} className="py-2 text-left font-weight-bold pl-4" style={{ color: '#0f172a' }}>{cat.category || cat.name}</td>
                                  <td align="center" colSpan={3} className="py-2">{cat.weightage || `${cat.weight}%`}</td>
                                  <td align="center" colSpan={4} className="py-2 font-weight-bold" style={{ backgroundColor: getScoreColor(cat.portfolio_avg_score_raw || cat.avg_score), color: '#0f172a' }} bgcolor={getScoreColor(cat.portfolio_avg_score_raw || cat.avg_score)}>{cat.portfolio_avg_score || cat.avg_score}</td>
                                  <td align="center" colSpan={3} className="py-2 font-weight-bold" style={{ color: cat.rag_code === 'red' ? '#dc2626' : (cat.rag_code === 'amber' ? '#ca8a04' : '#16a34a') }}>
                                    {cat.rag || 'Red'}
                                  </td>
                                </tr>
                              ))}
                              <tr>
                                <td colSpan={5} className="py-3 text-right pr-4 font-weight-bold" style={{ backgroundColor: '#fef2f2', color: '#b91c1c' }} bgcolor="#fef2f2">
                                  Weakest category org-wide:
                                </td>
                                <td colSpan={9} className="py-3 text-left pl-4 font-weight-bold" style={{ backgroundColor: '#fef2f2', color: '#b91c1c' }} bgcolor="#fef2f2">
                                  {data?.portfolio_snapshot?.weakest_category?.category || data?.category_level_risk?.weakest_category_org_wide?.category || '-'} 
                                  <span className="mx-2" style={{ opacity: 0.5 }}>|</span> 
                                  Code: {data?.portfolio_snapshot?.weakest_category?.code || data?.category_level_risk?.weakest_category_org_wide?.code || '-'} 
                                  <span className="mx-2" style={{ opacity: 0.5 }}>|</span> 
                                  Score: {data?.portfolio_snapshot?.weakest_category?.portfolio_avg_score || data?.category_level_risk?.weakest_category_org_wide?.portfolio_avg_score || '-'}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Qualitative Observations Modal */}
      <Modal 
        show={showObservationModal} 
        onHide={() => setShowObservationModal(false)} 
        size="lg" 
        centered
      >
        <Modal.Header closeButton className="border-bottom">
          <div>
            <Modal.Title className="font-weight-bold mb-1 rcr-modal-title">
              {selectedBranchObservations?.branch_name || selectedBranchObservations?.name || "Branch Observations"}
            </Modal.Title>
            <div className="rcr-modal-meta">
              {selectedBranchObservations?.audit_code && (
                <span className="badge badge-light mr-2 font-weight-bold px-2 py-1 rcr-modal-audit-code">
                  {selectedBranchObservations.audit_code}
                </span>
              )}
              {selectedBranchObservations?.audit_date && (
                <span className="mr-3">
                  <i className="las la-calendar mr-1"></i>{selectedBranchObservations.audit_date}
                </span>
              )}
              {selectedBranchObservations?.audit_result && (
                <span className={`badge font-weight-bold px-2 py-1 ${selectedBranchObservations.audit_result === 'PASS' ? 'rcr-modal-result-pass' : 'rcr-modal-result-fail'}`}>
                  {selectedBranchObservations.audit_result}
                </span>
              )}
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="p-4" style={{ backgroundColor: '#f8fafc' }}>
          {/* 1. Audit Observations */}
          <div className="rcr-modal-section">
            <div className="rcr-modal-section-header">
              AUDIT OBSERVATIONS
            </div>
            <div className="rcr-modal-section-body">
              {selectedBranchObservations?.audit_features || selectedBranchObservations?.qualitative?.audit_features || "None"}
            </div>
          </div>

          {/* Audit Exceptions (if available) */}
          {(selectedBranchObservations?.exceptions || selectedBranchObservations?.qualitative?.exceptions) && (
            <div className="rcr-modal-section">
              <div className="rcr-modal-section-header">
                AUDIT EXCEPTIONS
              </div>
              <div className="rcr-modal-section-body">
                {selectedBranchObservations?.exceptions || selectedBranchObservations?.qualitative?.exceptions}
              </div>
            </div>
          )}

          {/* 2. Appreciations, Issues & Call for Improvement */}
          <div className="rcr-modal-section mb-2">
            <div className="rcr-modal-section-header">
              APPRECIATIONS, ISSUES & CALL FOR IMPROVEMENT
            </div>
            <div className="rcr-modal-section-body">
              {selectedBranchObservations?.appreciations_improvements || selectedBranchObservations?.qualitative?.appreciations_improvements || "None"}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-top">
          <button 
            type="button" 
            className="btn btn-secondary font-weight-bold px-4 rounded-pill" 
            onClick={() => setShowObservationModal(false)}
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default RcrLeaderboardReport;
